import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import classNames from 'classnames';

import { convertUrlToFile } from '~libs/dataUtils';

import { getFile, getOne, uploadInvoiceFile } from '~support-at-home/actions/supplierInvoices';

import { clearCurrentInvoice } from '~support-at-home/reducers/supplierInvoices';

import {
  selectAllFiles,
  selectCurrentFile,
  selectCurrentSupplierInvoice,
  selectCurrentSupplierLoading,
} from '~support-at-home/selectors/supplierInvoices';

import FileOffCanvas from '../../common/FileOffCanvas';
import SupplierInvoiceModal from './SupplierInvoiceModal';

interface ComponentProps {
  id?: string;
  setId: (val: any) => void;
  load: () => void;
}

const EditSupplierInvoice: React.FC<ComponentProps> = ({ id, setId, load }) => {
  const dispatch = useAppDispatch();
  const supplierInvoice = useAppSelector(selectCurrentSupplierInvoice);
  const supplierInvoiceLoading = useAppSelector(selectCurrentSupplierLoading);
  const file = useAppSelector(selectCurrentFile);
  const files = useAppSelector(selectAllFiles) || [];
  const [isFileCanvasCollapsed, setIsFileCanvasCollapsed] = useState<boolean>(false);
  const isLoaded = supplierInvoiceLoading === 'fulfilled';

  const onClose = async (confirm: boolean, id?: string) => {
    setId(null);

    if (confirm && file && id) {
      if (!file || !file.data || !file.file_name) {
        return;
      }
      const blob = await convertUrlToFile(file.data, file.file_name);
      if (blob) {
        const newFile = new File([blob], file.file_name, { type: file.file_type });
        dispatch(uploadInvoiceFile({ file: newFile, id }));
      }
    }

    dispatch(clearCurrentInvoice({}));
    load();
  };

  useEffect(() => {
    if (id) {
      dispatch(getOne({ id }));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const loadFile = async () => {
      if (isLoaded) {
        const invoice_file = supplierInvoice?.file;
        if (invoice_file) {
          await dispatch(getFile({ id: invoice_file.id }));
        }
      }
    };

    loadFile();
  }, [id, isLoaded, supplierInvoice]);

  const handleUpload = async (file: any) => {
    // Dispatch the upload action with the new file
    const { payload } = await dispatch(uploadInvoiceFile({ file, id: id ?? '' }));
    if (payload?.success) {
      dispatch(getOne({ id }));
    }
  };

  const handleFileDeleted = () => {
    // Refresh invoice data after file deletion
    if (id) {
      dispatch(getOne({ id }));
    }
  };

  const modalClassName = classNames({
    'full-view': isFileCanvasCollapsed,
  });

  return (
    <>
      <SupplierInvoiceModal className={modalClassName} isOpen={!!id} onClose={onClose} isNew={false} />
      <FileOffCanvas
        isOpen={!!id}
        onChange={(file) => {
          handleUpload(file);
        }}
        onToggleCollapse={setIsFileCanvasCollapsed}
        isExisting={true}
        selectorFiles={files}
        onFileDeleted={handleFileDeleted}
      />
    </>
  );
};

export default EditSupplierInvoice;
